import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import HeadingCp from "../components/HeadingCp";
import Badge from "../components/Badge";

// Safe number coercion
const toNum = (v, d = 0) => {
  const n = typeof v === "string" ? v.replace(/[, ]+/g, "") : v;
  const f = Number(n);
  return Number.isFinite(f) ? f : d;
};

export default function UnitPricingBuilder() {
  const apiUrl = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();
  // Catalog
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);

  // Header (estimate) fields
  const [hdr, setHdr] = useState({
    company: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    company_address: "",
    date: new Date().toISOString().slice(0, 10),
  });
  const handleHdr = (k, v) => setHdr((p) => ({ ...p, [k]: v }));

  // Builder controls (left panel)
  const [selectedCat, setSelectedCat] = useState("-");
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [qtyInput, setQtyInput] = useState("1");
  const [taxRate, setTaxRate] = useState("0");
  const [proposalNotes, setProposalNotes] = useState("");

  // Picked items (right panel “cart”)
  const [picked, setPicked] = useState([]); // [{id, cat, device, description, unit_price, sku, manufacturer, quantity, line_total}]

  // Load catalog
  useEffect(() => {
    const fetchCatalog = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${apiUrl}/unit_pricing_crud.php?catalog=1`, {
          withCredentials: true,
        });
        if (!Array.isArray(data.items)) throw new Error("Bad catalog format");
        const normalized = data.items?.map((r, i) => ({
          id: r.id ?? i + 1,
          cat: String(r.cat || "").trim(),
          device: String(r.device || r.item || "").trim(),
          description: String(r.description || "").trim(),
          unit_price: toNum(r.unit_price ?? r.price ?? 0),
          sku: String(r.sku || "").trim(),
          manufacturer: String(r.manufacturer || "").trim(),
          notes: String(r.notes || "").trim(),
        }));
        setCatalog(normalized);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load unit catalog", { position: "top-center" });
      } finally {
        setLoading(false);
      }
    };
    fetchCatalog();
  }, [apiUrl]);

  // Category options
  const categories = useMemo(() => {
    const set = new Set();
    catalog.forEach((r) => r.cat && set.add(r.cat));
    return ["-", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [catalog]);

  // Device options filtered by selected category
  const deviceOptions = useMemo(() => {
    const rows = selectedCat === "ALL" ? catalog : catalog.filter((r) => r.cat === selectedCat);
    return rows.sort((a, b) => a.device.localeCompare(b.device));
  }, [catalog, selectedCat]);

  // Compute totals from picked list
  const subTotal = useMemo(
    () => +picked.reduce((s, li) => s + toNum(li.line_total, 0), 0).toFixed(2),
    [picked]
  );

  const taxPct = toNum(taxRate, 0);
  const tax = +((subTotal * taxPct) / 100).toFixed(2);
  const grandTotal = +(subTotal + tax).toFixed(2);

  // Add item to picked list
  const handleAddItem = () => {
    if (!selectedDeviceId) {
      toast.error("Pick a device first.", { position: "top-center" });
      return;
    }
    const base = catalog.find((r) => String(r.id) === String(selectedDeviceId));
    if (!base) {
      toast.error("Selected device not found.", { position: "top-center" });
      return;
    }
    const qty = Math.max(0, parseInt(qtyInput, 10) || 0);
    if (qty <= 0) {
      toast.error("Quantity must be greater than 0.", { position: "top-center" });
      return;
    }

    setPicked((prev) => {
      const idx = prev.findIndex((p) => String(p.id) === String(base.id));
      if (idx >= 0) {
        // increment existing
        const next = [...prev];
        const newQty = next[idx].quantity + qty;
        next[idx] = {
          ...next[idx],
          quantity: newQty,
          line_total: +(newQty * toNum(base.unit_price, 0)).toFixed(2),
        };
        return next;
      } else {
        return [
          ...prev,
          {
            ...base,
            quantity: qty,
            line_total: +(qty * toNum(base.unit_price, 0)).toFixed(2),
          },
        ];
      }
    });

    // UX: keep category, reset device & quantity to 1
    setSelectedDeviceId("");
    setQtyInput("1");
  };

  // Update qty inline in picked list
  const updatePickedQty = (id, v) => {
    const n = v === "" ? "" : Math.max(0, parseInt(v, 10) || 0);
    setPicked((prev) =>
      prev
        .map((p) => {
          if (String(p.id) !== String(id)) return p;
          const q = n === "" ? 0 : n;
          return {
            ...p,
            quantity: q,
            line_total: +(q * toNum(p.unit_price, 0)).toFixed(2),
          };
        })
        .filter((p) => p.quantity > 0) // remove if 0
    );
  };

  const removePicked = (id) => {
    setPicked((prev) => prev.filter((p) => String(p.id) !== String(id)));
  };

  const clearPicked = () => setPicked([]);

  // Build payloads
  const toEquipmentList = () =>
    picked.map((p) => ({
      cat: p.cat,
      device: p.device,
      description: p.description,
      quantity: p.quantity,
      unit_price: toNum(p.unit_price, 0),
      sku: p.sku,
      manufacturer: p.manufacturer,
    }));

  const makeProposalPayload = () => ({
    equipment_list: toEquipmentList(),
    notes: proposalNotes,
    totals: { subTotal, taxRate: taxPct, tax, grandTotal },
  });

  const handleGenerateProposal = () => {
    if (picked.length === 0) {
      toast.error("Add at least one item.", { position: "top-center" });
      return;
    }
    const payload = makeProposalPayload();
    toast.success("Proposal generated (check console).", { position: "top-center" });
  };

  const handleSaveEstimate = async () => {
    if (!hdr.company || !hdr.contact_email) {
      toast.error("Company and Contact Email are required.", { position: "top-center" });
      return;
    }
    if (picked.length === 0) {
      toast.error("Add at least one item.", { position: "top-center" });
      return;
    }

    const payload = {
      status: "save_estimate",
      header: {
        ...hdr,
        subtotal: subTotal,
        tax_rate: taxPct,
        tax,
        total: grandTotal,
        notes: proposalNotes,
      },
      items: picked.map((p) => ({
        unit_item_id: p.id,
        device: p.device,
        description: p.description,
        cat: p.cat,
        sku: p.sku,
        manufacturer: p.manufacturer,
        unit_price: toNum(p.unit_price, 0),
        quantity: p.quantity,
        line_total: toNum(p.line_total, 0),
      })),
    };

    try {
      const { data } = await axios.post(`${apiUrl}/unit_pricing_crud.php`, payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      if (data?.success) {
        toast.success(`Estimate #${data.estimate_id} saved!`, { position: "top-center" });
      } else {
        toast.error(data?.error || "Save failed", { position: "top-center" });
      }
    } catch (e) {
      console.error(e);
      toast.error("Save failed", { position: "top-center" });
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] p-4">
      <HeadingCp label="Build Unit Pricing Proposals" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-1">
        {/* LEFT PANEL */}
        <div className="border rounded-lg p-4 bg-white">
          {/* Customer / Header */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
            <input
              className="border rounded px-3 py-2"
              placeholder="Company"
              value={hdr.company}
              onChange={(e) => handleHdr("company", e.target.value)}
            />         
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-1 mb-4">
          <input
            className="border rounded px-3 py-2"
            placeholder="Company Address"
            value={hdr.company_address}
            onChange={(e) => handleHdr("company_address", e.target.value)}
          />   
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <input
              className="border rounded px-3 py-2"
              placeholder="Contact Name"
              value={hdr.contact_name}
              onChange={(e) => handleHdr("contact_name", e.target.value)}
            />
            <input
              className="border rounded px-3 py-2"
              placeholder="Contact Email"
              value={hdr.contact_email}
              onChange={(e) => handleHdr("contact_email", e.target.value)}
            />

            <input
              className="border rounded px-3 py-2"
              placeholder="Contact Phone"
              value={hdr.contact_phone}
              onChange={(e) => handleHdr("contact_phone", e.target.value)}
            />
            </div>                    

          <br/>
          {/* Cascading dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Category</label>
              <select className="border rounded px-3 py-2 w-full"
                value={selectedCat} onChange={(e) => { setSelectedCat(e.target.value); setSelectedDeviceId(""); }}>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Device</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={selectedDeviceId}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                disabled={deviceOptions.length === 0 && categories.length > 1}
              >
                <option value="">Select a device…</option>
                {deviceOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.device} {d.sku ? `(${d.sku})` : ""} — ${toNum(d.unit_price, 0).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-1">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Qty</label>
              <input
                type="number"
                min={1}
                value={qtyInput}
                onChange={(e) => setQtyInput(e.target.value)}
                className="border rounded px-3 py-2 w-full text-right"
              />
            </div>
          </div>
            
          <br/>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={handleAddItem}
              className="bg-emerald-600 text-white px-3 py-2 rounded shadow hover:bg-emerald-700 disabled:opacity-50"
              disabled={loading || !selectedDeviceId}
            >
              {loading ? "Loading…" : "Add Item"}
            </button>
            <button
              onClick={clearPicked}
              className="bg-gray-100 text-gray-800 px-3 py-2 rounded shadow hover:bg-gray-200 disabled:opacity-50"
              disabled={picked.length === 0}
            >
              Clear List
            </button>
          </div>

          {/* Tax + Notes + Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Tax %</label>
              <input
                type="number"
                min={0}
                step="0.001"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                className="border rounded px-3 py-2 w-full"
                placeholder="Tax %"
                title="Sales tax percent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Proposal Notes (optional)</label>
              <textarea
                value={proposalNotes}
                onChange={(e) => setProposalNotes(e.target.value)}
                rows={3}
                className="w-full border rounded px-3 py-2"
                placeholder="Add qualifications/terms or any custom notes."
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {/* <button
              onClick={handleGenerateProposal}
              className="bg-indigo-600 text-white px-3 py-2 rounded shadow hover:bg-indigo-700 disabled:opacity-50"
              disabled={picked.length === 0}
            >
              Generate Proposal
            </button> */}
            <button
              onClick={handleSaveEstimate}
              className="bg-blue-600 text-white px-3 py-2 rounded shadow hover:bg-blue-700 disabled:opacity-50"
              disabled={picked.length === 0}
            >
              Save Estimate
            </button>
              <button
                type="button"
                onClick={() => navigate("/unit_pricing_estimate_list")}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-400 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                Estimate List
              </button>              
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Picked Items</h2>
            <div className="text-sm text-gray-500">
              {picked.length} item{picked.length !== 1 ? "s" : ""}
            </div>
          </div>

          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr className="text-left">
                  <th className="px-3 py-2">Device</th>
                  <th className="px-3 py-2 text-right">Unit</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Line</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {picked.map((p) => (
                  <tr key={p.id} className="border-t">

                    <td className="px-3 py-2">
                      <div className="font-medium">
                        <Badge color="blue" feed={p.cat}/><br/>
                        {p.device}
                      </div>
                      {p.description ? (
                        <div className="text-gray-500">{p.description}</div>
                      ) : null}
                    </td>

                    <td className="px-3 py-2 text-right">
                      ${toNum(p.unit_price, 0).toFixed(2)}
                    </td>

                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min={0}
                        value={p.quantity}
                        onChange={(e) => updatePickedQty(p.id, e.target.value)}
                        className="w-20 text-right border rounded px-2 py-1"
                      />
                    </td>

                    <td className="px-3 py-2 text-right font-semibold">
                      ${toNum(p.line_total, 0).toFixed(2)}
                    </td>

                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => removePicked(p.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove">
                        &times;
                      </button>
                    </td>

                  </tr>
                ))}
                {picked.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                      {loading ? "Loading catalog…" : "No items added yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-4 border rounded p-4 bg-gray-50">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-semibold">${subTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Tax ({taxPct.toFixed(3)}%)</span>
              <span className="font-semibold">${tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg">
              <span className="font-bold">Total</span>
              <span className="font-extrabold">${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
